import { EntityResourcePermissionDAO, ListResponse } from "./EntityResourcePermissionDAO";
import {
    PermissionEntityType,
    PermissionResourceType
} from "../../../API";

import ClientLogger from "../../logging/ClientLogger";
import { DataStoreClass } from "@aws-amplify/datastore";
import { EntityResourcePermission } from "../../../models";
import { ListEntityResourcePermissionError } from "../errors/ListEntityResourcePermissionError";

export class DataStoreEntityResourcePermissionDAO implements EntityResourcePermissionDAO {
    private static LIST_BY_ENTITY_AND_RESOURCE_ATTEMPT = "ListEntityResourcePermissionsByEntityAndResourceIdAttempt"
    private static LIST_BY_ENTITY_AND_RESOURCE_FAILURE = "ListEntityResourcePermissionsByEntityAndResourceIdFailure"
    private static LIST_BY_RESOURCE_ID_AND_TYPE_ATTEMPT = "ListEntityResourcePermissionsByResourceIdAndTypeAttempt"
    private static LIST_BY_RESOURCE_ID_AND_TYPE_FAILURE = "ListEntityResourcePermissionsByResourceIdAndTypeFailure"

    private logger: ClientLogger;
    private readonly dataStore: DataStoreClass;

    constructor(
        logger: ClientLogger,
        dataStore: DataStoreClass,
    ) {
        this.logger = logger;
        this.dataStore = dataStore;
    }

    public async listByEntityAndResource(
        entityType: PermissionEntityType,
        entityId: string,
        resourceType: PermissionResourceType,
        resourceId: string,
        isGranted: boolean
    ): Promise<EntityResourcePermission[]> {
        this.logger.info(
            `Listing EntityResourcePermission Records with entityType: ${entityType}, entityId: ${entityId}, resourceType: ${resourceType}, resourceId: ${resourceId}, isGranted: ${isGranted}`,
            undefined,
            [DataStoreEntityResourcePermissionDAO.LIST_BY_ENTITY_AND_RESOURCE_ATTEMPT]
        );
        try {
            return await this.dataStore.query(
                EntityResourcePermission,
                erp => erp.entityType('eq', entityType)
                    .entityId('eq', entityId)
                    .resourceType('eq', resourceType)
                    .resourceId('eq', resourceId)
                    .isGranted('eq', isGranted)
            );
        } catch (error) {
            const errorMessage: string = `Unable to list EntityResourcePermission Records with entityType: ${entityType}, entityId: ${entityId}, resourceType: ${resourceType}, resourceId: ${resourceId}, isGranted: ${isGranted}`;
            this.logger.error(
                errorMessage,
                error,
                [DataStoreEntityResourcePermissionDAO.LIST_BY_ENTITY_AND_RESOURCE_FAILURE]
            );
            throw new ListEntityResourcePermissionError(errorMessage, error as Error);
        }
    }

    public async listByResourceIdAndResourceType(
        resourceId: string,
        resourceType: PermissionResourceType,
        isGranted: boolean,
        nextToken?: string
    ): Promise<ListResponse<EntityResourcePermission>> {
        this.logger.info(
            `Listing EntityResourcePermission Records with resourceId: ${resourceId}, resourceType: ${resourceType}, isGranted: ${isGranted}`,
            undefined,
            [DataStoreEntityResourcePermissionDAO.LIST_BY_RESOURCE_ID_AND_TYPE_ATTEMPT]
        );
        try {
            // DataStore doesn't support pagination with nextToken directly
            // Fetching all matching records and handling pagination in memory
            const items = await this.dataStore.query(
                EntityResourcePermission,
                erp => erp.resourceId('eq', resourceId)
                    .resourceType('eq', resourceType)
                    .isGranted('eq', isGranted)
            );

            // We don't have direct support for nextToken in DataStore,
            // so we return all results with no nextToken
            return {
                items,
                nextToken: undefined
            };
        } catch (error) {
            const errorMessage: string = `Unable to list EntityResourcePermission Records with resourceId: ${resourceId}, resourceType: ${resourceType}, isGranted: ${isGranted}`;
            this.logger.error(
                errorMessage,
                error,
                [DataStoreEntityResourcePermissionDAO.LIST_BY_RESOURCE_ID_AND_TYPE_FAILURE]
            );
            throw new ListEntityResourcePermissionError(errorMessage, error as Error);
        }
    }
}